/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Trash2, Pill, Download } from 'lucide-react';
import { deleteDocument } from '@/lib/medical';
import { cn } from '@/lib/utils';
import { DOC_TYPE_META } from './WalletCard';

// ---------- Detail Dialog (view / download / delete) ----------
export const DetailDialog = ({ item, kind, onClose, onDeleted }) => {
  const { t } = useTranslation();
  const [acting, setActing] = useState(false);
  if (!item) return null;
  const meta = kind === 'prescription'
    ? { label: 'Prescrizione', gradient: 'from-primary to-blue-600', icon: Pill }
    : DOC_TYPE_META[item.doc_type] || DOC_TYPE_META.altro;

  const download = () => {
    const b64 = kind === 'prescription' ? item.pdf_base64 : item.file_base64;
    if (!b64) return;
    const a = document.createElement('a');
    a.href = b64;
    a.download = kind === 'prescription' ? `prescrizione_${item.code}.pdf` : (item.file_name || `documento_${item.code}`);
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };
  const del = async () => {
    if (kind !== 'document' || acting) return;
    if (!window.confirm(t('walletConfirmDelete'))) return;
    setActing(true);
    try { await deleteDocument(item.id); toast.success(t('walletDeleted')); onDeleted?.(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('genericError')); }
    finally { setActing(false); }
  };
  const dateStr = new Date(item.issued_at || item.created_at).toLocaleString('it');

  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="wallet-detail-dialog">
        <div className={cn('relative p-6 text-white bg-gradient-to-br', meta.gradient)}>
          <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-white/10 blur-2xl pointer-events-none" />
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-xl">{kind === 'prescription' ? (item.diagnosis || 'Prescrizione') : item.title}</DialogTitle>
            <DialogDescription className="text-white/80">{dateStr}</DialogDescription>
          </DialogHeader>
          <div className="mt-3 flex items-center gap-3">
            {item.qr_code_base64 && <img src={item.qr_code_base64} className="h-24 w-24 rounded-xl bg-white p-1" alt="QR" data-testid="wallet-detail-qr" />}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest opacity-75">Codice</div>
              <div className="font-mono font-bold text-sm truncate">{item.code}</div>
              <div className="text-xs opacity-75 mt-2">{meta.label}</div>
            </div>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {kind === 'prescription' && (
            <div className="space-y-3">
              {item.drugs?.map((d, idx) => (
                <div key={idx} className="rounded-2xl border border-border/60 p-3">
                  <div className="font-semibold flex items-center gap-2"><Pill className="h-4 w-4 text-primary" /> {d.name}</div>
                  <div className="text-xs text-muted-foreground mt-1 grid grid-cols-2 gap-1">
                    {d.dosage && <div><b>Dosaggio:</b> {d.dosage}</div>}
                    {d.frequency && <div><b>Frequenza:</b> {d.frequency}</div>}
                    {d.duration && <div><b>Durata:</b> {d.duration}</div>}
                  </div>
                  {d.notes && <div className="text-xs mt-2 text-muted-foreground">{d.notes}</div>}
                </div>
              ))}
              {item.notes && <div className="text-sm rounded-xl bg-muted p-3"><b>Note:</b> {item.notes}</div>}
            </div>
          )}
          {kind === 'document' && item.description && (
            <p className="text-sm text-muted-foreground">{item.description}</p>
          )}
          <div className="flex gap-2 justify-end pt-2">
            {kind === 'document' && (
              <Button variant="ghost" size="sm" onClick={del} disabled={acting} className="text-destructive hover:text-destructive" data-testid="wallet-detail-delete-btn">
                <Trash2 className="h-4 w-4" /> {t('delete')}
              </Button>
            )}
            <Button onClick={download} data-testid="wallet-detail-download-btn">
              <Download className="h-4 w-4" /> {t('walletDownload')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
